import { PODetails, ShippingAddress } from '../../models/po';
import {
  BuyerPoShippingAddress,
  BuyerPurchaseOrder,
  BuyerRfqPoDetails,
} from '../../models/po-rfq';

/**
 * Maps one purchase order out of the RFQ-scoped payload onto the `PODetails`
 * shape the per-purchase-order screens already render.
 *
 * The buyer's purchase-order route is now keyed by RFQ, so there is no endpoint
 * left that answers a single purchase order. This adapter is what keeps the
 * payment and delivery-confirmation screens working off the RFQ payload instead
 * of a rewrite.
 */

/** The RFQ payload sends country and city as plain strings; the legacy shape wants objects. */
const toShippingAddress = (
  address: BuyerPoShippingAddress | undefined
): ShippingAddress =>
  ({
    building_no: address?.building_no ? Number(address.building_no) : undefined,
    governorate: address?.governorate ?? { id: 0, name: '' },
    district: address?.district,
    city: { id: 0, name: address?.city ?? '' },
    country: {
      name: address?.country ?? '',
      alpha2code: address?.country ?? '',
      calling_code: '',
      flag: '',
    },
    full_address: address?.full_address,
  }) as ShippingAddress;

export const toPoDetails = (
  rfq: BuyerRfqPoDetails,
  po: BuyerPurchaseOrder
): PODetails => ({
  id: po.id,
  state: po.state,
  buyer_name: rfq.buyer?.name,
  supplier_name: po.supplier?.name,
  buyer_logo: rfq.buyer?.logo,
  supplier_logo: po.supplier?.logo,
  buyer_country: rfq.buyer?.country ?? null,
  supplier_country: po.supplier?.country ?? null,
  currency: po.currency,
  quotation_id: po.quotation_id,
  groups_count: rfq.groups_count,
  actual_delivery_date: po.actual_delivery_date ?? undefined,
  /**
   * Either name the payload uses: the resource documents `is_hold` and this app
   * types `admin_hold`, and the delivery card below reads one field. Dropped
   * here, the "under review by our team" notice could never appear on this
   * screen — the one place a disputed order is followed.
   */
  admin_hold: po.admin_hold ?? po.is_hold,
  inspection: po.inspection,
  po_document: po.po_document,
  can_confirm_delivery: po.can_confirm_delivery,
  payment: undefined,
  payment_plan: po.payment_plan,
  payment_summary: po.total_price
    ? { groups_count: rfq.groups_count, total_price: po.total_price }
    : undefined,
  rfq: {
    id: rfq.id,
    general_terms: rfq.general_terms ?? undefined,
    delivery_terms: rfq.delivery_terms ?? undefined,
    cancellation_terms: rfq.cancellation_terms ?? undefined,
    /** Shipping and delivery are per purchase order now, so prefer the order's own. */
    delivery_date: po.delivery_date ?? rfq.delivery_date ?? undefined,
    created_at: rfq.created_at,
    upload_documents: rfq.upload_documents,
    shipping_address: toShippingAddress(po.shipping_address),
  } as PODetails['rfq'],
});

/** Finds one purchase order across every group of the RFQ payload. */
export const findPurchaseOrder = (
  rfq: BuyerRfqPoDetails | undefined,
  poId: number
): BuyerPurchaseOrder | undefined =>
  rfq?.groups
    .flatMap(group => group.purchase_orders)
    .find(purchaseOrder => purchaseOrder.id === poId);
